'use client';

import { Suspense, useEffect, type CSSProperties } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { CreateMatterProvider } from '@/lib/contexts/create-matter-context';
import { QueryProvider } from '@/lib/providers/query-provider';
import { UserProvider } from '@/lib/contexts/user-context';
import { UserProfileProvider } from '@/lib/providers/user-profile-provider';
import { FeatureFlagsProvider } from '@/lib/providers/feature-flags-provider';
import { NavigationLoadingProvider } from '@/lib/contexts/navigation-loading-context';
import { AuthenticatedAppSidebar } from '@/components/app-sidebar/authenticated-app-sidebar';
import { SidebarInset, SidebarProvider } from '@/components/ui/sidebar';
import { LayoutWrapper } from '@/components/layout-wrapper';
import { CTADialogProvider } from '@/lib/services/tasks/cta-dialog-context';
import { CorrespondenceWorkflowWrapper } from '@/components/workflows/correspondence-workflow-wrapper';
import { DevFeatureFlagToggle } from '@/components/dev-tools/dev-feature-flag-toggle';
import { UploadRecovery } from '@/components/ai-scribe/recording-flow/upload-recovery';
import { RecordingCompletionToaster } from '@/components/ai-scribe/recording-completion-toaster';
import { IntercomMessenger } from '@/components/intercom/intercom-messenger';
// LF-4086: the v2 activation gate is the permanent, data-driven activation page
// for all firms (v1 `TrialActivationGate` retired). It gates pending firms by
// default (opt-out per-firm) and active-free firms on opt-in.
import { ActivationV2Gate } from '@/components/auth/activation-v2-gate';
import {
  getMatterListNavigationActions,
  isMatterListId,
  getAccountNavigationActions
} from '@/lib/stores';

/**
 * Tracks the active matter list view from the URL.
 * When the user is on `/?view=today` (or upcoming/anytime/all/archive),
 * this updates the navigation store so the back button on detail pages
 * can deterministically navigate to the correct view.
 */
function MatterListViewTracker() {
  const pathname = usePathname();

  useEffect(() => {
    if (!pathname) return;
    // Extract view from pathname (e.g. '/today' → 'today')
    const view = pathname === '/' ? 'all' : pathname.replace('/', '');
    if (isMatterListId(view)) {
      getMatterListNavigationActions().setActiveMatterList(view);
    }
  }, [pathname]);

  return null;
}

// Path prefixes that are transient setup/auth flows — returning to them
// after completion shows an error (e.g. /integrations/smokeball/configure
// errors once Smokeball is already connected).
const TRANSIENT_PATH_PREFIXES = ['/integrations/'];

/**
 * Tracks the last non-account path so the account sidebar back button
 * can return the user to where they came from.
 */
function AccountReturnPathTracker() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    if (!pathname || pathname.startsWith('/account')) return;
    if (TRANSIENT_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix)))
      return;
    const search = searchParams.toString();
    const fullPath = search ? `${pathname}?${search}` : pathname;
    getAccountNavigationActions().setReturnPath(fullPath);
  }, [pathname, searchParams]);

  return null;
}

interface ProvidersWrapperProps {
  children: React.ReactNode;
}

// List of public paths that don't need authentication.
// NOTE: this list is duplicated in `proxy.ts` `publicPaths` — they have
// different responsibilities (proxy gates the request; this gates which
// providers wrap the tree) but must stay in sync. When adding a new
// unauthenticated route, edit BOTH lists.
const PUBLIC_PATHS = [
  '/login',
  '/nextauth-login',
  '/accept-invitation',
  '/get-started',
  '/auth-error',
  '/callback',
  '/test-invitation',
  '/auth-redirect',
  '/portal',
  // LF-3743 — AI Scribe QR-handoff mobile capture surface. Auth via
  // RecordingHandoffAuthGuard on the BE; the FE has no NextAuth session.
  // Wrapping the tree with UserProfileProvider would trigger an
  // authenticated profile fetch → 401 → redirect to /login.
  '/device-recording',
  // VALE-134: public, unauthenticated 2025 Probate Report lead-capture page.
  '/download-2025-probate-report',
  // VALE-407: the Microsoft 365 consent disclosure a firm's EXTERNAL Microsoft
  // administrator lands on. Same reason as `/device-recording` above — the reader
  // has no NextAuth session, so `UserProfileProvider` would fire an authenticated
  // profile fetch, take a 401, and federated-log-out to /login. The page 200s
  // either way, which is what makes this failure invisible to a status check.
  '/m365-authorisation'
];

// NOTE: this list has a sibling in `proxy.ts` (`publicPaths`), which gates the
// middleware's NextAuth check. They answer different questions — "may this request
// through without a session" vs "may this tree mount authenticated providers" — but
// a new public route needs BOTH, and adding it to only one fails in a way no
// status code reveals. Grep for the other whenever you touch either.

export function ProvidersWrapper({ children }: ProvidersWrapperProps) {
  const pathname = usePathname();

  // Check if current path is public
  const isPublicPath = PUBLIC_PATHS.some((path) => pathname?.startsWith(path));

  // Details page has its own sidebar (IconRail + DetailsSidebar), don't wrap with app sidebar
  const isDetailsPage = pathname?.startsWith('/matters');

  // Account page has its own SidebarLayout, don't wrap with app sidebar
  const isAccountPage = pathname?.startsWith('/account');

  // /onboarding (the first-sign-in flow) renders full-screen without the
  // app sidebar. /onboarding/get-started (the hub) and any other future
  // /onboarding/* sub-route falls through to the default branch and gets
  // the sidebar.
  const isOnboardingFlow = pathname === '/onboarding';

  // True for routes that render their own sidebar/layout shell — wrapping
  // them in the app sidebar here would nest two layouts. Matters' dashboard
  // views now render through the shared shell too (LF-5279), so they are no
  // longer skipped here.
  const skipSidebar = isDetailsPage || isAccountPage || isOnboardingFlow;

  // Always provide QueryClient - critical for preventing "No QueryClient set" errors
  return (
    <QueryProvider>
      {isPublicPath ? (
        // For public paths, only provide QueryClient and render children directly
        <>{children}</>
      ) : (
        // Single authed provider tree. The sidebar wrapper toggles inside —
        // swapping the entire branch (as we used to) caused React to remount
        // the provider subtree, which surfaced as a "Rendered more hooks"
        // error in the app router during /onboarding → /onboarding/get-started.
        <UserProfileProvider>
          <FeatureFlagsProvider>
            <UserProvider>
              <NavigationLoadingProvider>
                <Suspense>
                  <MatterListViewTracker />
                  <AccountReturnPathTracker />
                </Suspense>
                <CreateMatterProvider>
                  <CTADialogProvider>
                    {/* Activation gate (LF-4086): the data-driven "Welcome to
                        Vale" page. Gates pending firms by default (opt-out
                        per-firm via `trial-gate-opt-out`) and active-free firms
                        on opt-in (`trial-gate-opt-in`); every other firm renders
                        the app untouched. */}
                    <ActivationV2Gate>
                      {skipSidebar ? (
                        children
                      ) : (
                        <SidebarProvider
                          style={
                            {
                              '--sidebar-width': '16rem',
                              '--sidebar-width-icon': '4rem'
                            } as CSSProperties
                          }
                        >
                          <AuthenticatedAppSidebar />
                          {/* This is the single shared shell for all top-level
                              pages (Matters, Scribe, Intake, Templates) via
                              LayoutWrapper (LF-5279). Bound the height and
                              neutralise the sidebar's `variant="inset"` chrome
                              (`m-2`/`rounded-xl`/`shadow-sm`) so pages render
                              flush and full-height. Without this the inset
                              margin pushes the inset past the viewport so the
                              *window* scrolls (native bar) instead of the inner
                              shared ScrollArea, and the rounded card frames the
                              content as an inset panel rather than flush. */}
                          <SidebarInset className="!m-0 h-screen min-w-0 overflow-hidden !rounded-none !shadow-none">
                            <LayoutWrapper>{children}</LayoutWrapper>
                          </SidebarInset>
                        </SidebarProvider>
                      )}
                    </ActivationV2Gate>
                    <CorrespondenceWorkflowWrapper />
                    <IntercomMessenger />
                  </CTADialogProvider>
                </CreateMatterProvider>
              </NavigationLoadingProvider>
            </UserProvider>
            <DevFeatureFlagToggle />
          </FeatureFlagsProvider>
        </UserProfileProvider>
      )}
      {/* Recording components live outside the conditional branches so they
          survive navigation between route types (dashboard ↔ sidebar pages) */}
      {!isPublicPath && (
        <>
          <UploadRecovery />
          <RecordingCompletionToaster />
        </>
      )}
    </QueryProvider>
  );
}
